import React from 'react';

function MethodologyPage({ C, useBreakpoint }) {
	const { isMobile, isTablet, isDesktop, px } = useBreakpoint();
	const compact = isMobile || isTablet;

	const sections = [
		{
			n: '01',
			title: 'Conceptual Foundation',
			body: `CDM is built on a single question: which individuals should reasonably be expected to appear in later historical records, but do not, under conditions where disappearance is statistically unusual?\n\nThe model treats linkage failure not as evidence of death but as a signal requiring contextual interpretation. Only failures that occur under statistically unusual conditions are considered for inclusion.`,
		},
		{
			n: '02',
			title: 'Data Sources',
			body: `Individual records were identified through original 1920 U.S. Census images accessed via public genealogical archives. The census is a public domain record, the names, ages, and household information it contains belong to the historical record, not to any data provider.\n\nIPUMS full-count microdata (1920–1950) was used for population-level statistical analysis only, to construct the risk pool, apply demographic filters, and identify linkage failures at scale. IPUMS data has not been redistributed in any form.\n\nSupplemental sources for corroboration include state death certificate archives, church membership rolls, property records, newspaper archives, and family genealogical records submitted by descendants.`,
		},
		{
			n: '03',
			title: 'Risk Pool Construction',
			body: `Five sequential filters reduce over one million records to the risk pool:\n\n1. Race: Black individuals only\n2. Geography: Oklahoma residents, 1920\n3. Urban Status: Urban enumerations only\n4. Age: Prime-age cohorts 16–49\n5. Linkage: No successful forward census link to 1930 or later`,
		},
		{
			n: '04',
			title: 'Household Composition Analysis',
			body: `CDM examines disruption at the household level. Each household is categorized by which members are missing, producing 35 distinct disruption patterns classified by risk level.\n\nThree patterns are High risk: complete household disappearance in multi-member households, large mixed household disappearance, and single-member complete disappearance.`,
		},
		{
			n: '05',
			title: 'Ethical Constraints',
			body: `Individual names and identifying information will not be published without the consent of descendant communities and partnering institutions. CDM does not identify victims, it identifies candidates for further investigation.\n\nNo individual in the risk pool will be characterized as a massacre victim without independent corroboration from external historical sources. Any future publication of individual-level findings will cite original census records as the source, and will be undertaken only in close collaboration with Greenwood descendant organizations.`,
		},
		{
			n: '06',
			title: 'Limitations',
			body: `Linkage failure does not imply death. Many individuals in the risk pool will have migrated, changed names, or been missed by enumerators in 1930.\n\nBlack residents were systematically undercounted in early twentieth-century census records, meaning some killed or displaced may not appear in the 1920 baseline at all. The model can only analyze those it captured, it cannot account for those never enumerated.`,
		},
	];

	const navItems = sections.map((s) => s.title);

	return (
		<div style={{ paddingTop: 100 }}>
			{/* ── HEADER ── */}
			<div
				style={{
					padding: `60px ${px} 48px`,
					borderBottom: '1px solid #1A2E2A',
				}}
			>
				<div className='section-label fade-up' style={{ marginBottom: 20 }}>
					Methodology
				</div>
				<h1
					className='display-heading fade-up delay-1'
					style={{
						fontSize: compact ? 'clamp(30px,7vw,52px)' : 'clamp(38px,5vw,60px)',
						maxWidth: 660,
					}}
				>
					Direction,
					<br />
					<span style={{ color: C.teal }}>not declaration.</span>
				</h1>
			</div>

			{/* ── BODY ── */}
			<div
				style={{
					padding: `60px ${px}`,
					display: 'grid',
					gridTemplateColumns: isDesktop ? '220px 1fr' : '1fr',
					gap: compact ? 40 : 72,
				}}
			>
				{/* Sticky nav, desktop only */}
				{isDesktop && (
					<div style={{ position: 'sticky', top: 90, height: 'fit-content' }}>
						{navItems.map((item, i) => (
							<div
								key={item}
								style={{
									padding: '11px 0',
									borderBottom: '1px solid #1A2E2A',
									display: 'flex',
									gap: 12,
									alignItems: 'center',
								}}
							>
								<span
									style={{
										fontFamily: 'DM Mono',
										fontSize: 10,
										color: '#2A4A46',
										flexShrink: 0,
									}}
								>
									{String(i + 1).padStart(2, '0')}
								</span>
								<span
									style={{
										fontSize: 12,
										color: '#4A6A66',
										letterSpacing: '0.01em',
									}}
								>
									{item}
								</span>
							</div>
						))}

						{/* Data provenance note */}
						<div
							style={{
								marginTop: 32,
								padding: '16px',
								background: '#111E1C',
								border: '1px solid #1A2E2A',
							}}
						>
							<div
								style={{
									fontFamily: 'DM Mono',
									fontSize: 9,
									color: C.tealLight,
									letterSpacing: '0.14em',
									textTransform: 'uppercase',
									marginBottom: 8,
								}}
							>
								Primary Source
							</div>
							<div style={{ fontSize: 12, color: '#4A6A66', lineHeight: 1.7 }}>
								1920 U.S. Census Records
								<br />
								Public domain · genealogical archives
							</div>
							<div
								style={{
									marginTop: 10,
									paddingTop: 10,
									borderTop: '1px solid #1A2E2A',
									fontFamily: 'DM Mono',
									fontSize: 9,
									color: '#2A4A46',
									lineHeight: 1.6,
								}}
							>
								Statistical analysis assisted by IPUMS full-count microdata. Not
								redistributed.
							</div>
						</div>
					</div>
				)}

				{/* Section content */}
				<div>
					{sections.map(({ n, title, body }) => (
						<div
							key={n}
							style={{
								marginBottom: 52,
								paddingBottom: 52,
								borderBottom: '1px solid #1A2E2A',
							}}
						>
							<div
								style={{
									display: 'flex',
									gap: 14,
									alignItems: 'flex-start',
									marginBottom: 20,
								}}
							>
								<span
									style={{
										fontFamily: 'DM Mono',
										fontSize: compact ? 22 : 30,
										color: '#1A3A36',
										fontWeight: 500,
										lineHeight: 1,
										flexShrink: 0,
									}}
								>
									{n}
								</span>
								<h2
									style={{
										fontFamily: 'Playfair Display',
										fontSize: compact ? 20 : 26,
										color: 'white',
										lineHeight: 1.2,
									}}
								>
									{title}
								</h2>
							</div>
							{body.split('\n\n').map((para, i) => (
								<p
									key={i}
									style={{
										fontSize: compact ? 15 : 16,
										color: '#6A8A86',
										lineHeight: 1.9,
										marginBottom: 16,
									}}
								>
									{para}
								</p>
							))}
						</div>
					))}

					{/* Core limitation callout */}
					<div
						style={{
							background: '#111E1C',
							border: '1px solid #1A2E2A',
							borderLeft: `3px solid ${C.teal}`,
							padding: compact ? '24px' : '30px 34px',
						}}
					>
						<div
							style={{
								fontFamily: 'DM Mono',
								fontSize: 10,
								letterSpacing: '0.15em',
								textTransform: 'uppercase',
								color: C.tealLight,
								marginBottom: 14,
							}}
						>
							Core Limitation
						</div>
						<p
							style={{
								fontFamily: 'Playfair Display',
								fontStyle: 'italic',
								fontSize: compact ? 16 : 19,
								color: 'white',
								lineHeight: 1.7,
								margin: 0,
							}}
						>
							CDM cannot determine cause of death, distinguish between death and
							migration, or replace archival and oral historical work. Its value
							lies in direction, not declaration.
						</p>
					</div>
				</div>
			</div>
		</div>
	);
}

export default MethodologyPage;
